import { Button } from "@/components/ui/button";
import { Plus, X, Pencil } from "lucide-react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import type { CustomerCategory } from "@/hooks/useCustomerCategories";

export interface PricingCategory {
  key: string;
  label: string;
}

// Keep for backward compat but no longer primary
export const defaultCategories: PricingCategory[] = [
  { key: "standard", label: "Standard" },
  { key: "medlem", label: "Medlem" },
  { key: "senior", label: "Pensionär" },
  { key: "junior_student", label: "Junior/Student" },
];

interface Props {
  // DB-backed categories
  dbCategories?: CustomerCategory[];
  // Legacy fallback
  categories?: PricingCategory[];
  selected: string;
  onSelect: (key: string) => void;
  onAdd?: (name: string) => void;
  onRename?: (category: CustomerCategory, newName: string) => void;
  onRemove?: (category: CustomerCategory, moveTo?: string) => void;
  onGetDependencies?: (categoryId: string, slug: string) => Promise<{ customerCount: number; pricingRuleCount: number; activityPriceCount: number }>;
  onUpdateDailyLimit?: (category: CustomerCategory, dailyMinutesLimit: number | null, fallbackCategory: string, overflowPercent: number) => void;
  // Legacy callbacks
  onCategoriesChange?: (cats: PricingCategory[]) => void;
  editable?: boolean;
}

export default function PricingCategoryTabs({
  dbCategories,
  categories,
  selected,
  onSelect,
  onAdd,
  onRename,
  onRemove,
  onGetDependencies,
  onUpdateDailyLimit,
  onCategoriesChange,
  editable,
}: Props) {
  const [addOpen, setAddOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameCat, setRenameCat] = useState<CustomerCategory | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CustomerCategory | null>(null);
  const [moveToCategory, setMoveToCategory] = useState("");
  const [dependencies, setDependencies] = useState<{ customerCount: number; pricingRuleCount: number; activityPriceCount: number } | null>(null);
  const [loadingDeps, setLoadingDeps] = useState(false);
  const [dailyLimitEnabled, setDailyLimitEnabled] = useState(false);
  const [dailyLimitMinutes, setDailyLimitMinutes] = useState(90);
  const [dailyLimitFallback, setDailyLimitFallback] = useState("standard");
  const [overflowPercent, setOverflowPercent] = useState(100);

  // Use DB categories if available, otherwise fall back to legacy
  const useDb = !!dbCategories;
  const items: { key: string; label: string; isDefault: boolean; dbCat?: CustomerCategory }[] = useDb
    ? dbCategories!.map((c) => ({ key: c.slug, label: c.name, isDefault: c.is_default, dbCat: c }))
    : (categories ?? defaultCategories).map((c) => ({ key: c.key, label: c.label, isDefault: c.key === "standard" }));

  const handleAdd = () => {
    if (!newLabel.trim()) return;
    if (onAdd) {
      onAdd(newLabel.trim());
    } else if (onCategoriesChange && categories) {
      const key = newLabel.trim().toLowerCase().replace(/[^a-zåäö0-9]/g, "_");
      if (categories.some((c) => c.key === key)) return;
      onCategoriesChange([...categories, { key, label: newLabel.trim() }]);
    }
    setNewLabel("");
    setAddOpen(false);
  };

  const handleRemove = async (item: typeof items[0]) => {
    if (item.isDefault) return;

    // Legacy path (no DB categories)
    if (!item.dbCat) {
      if (onCategoriesChange && categories) {
        onCategoriesChange(categories.filter((c) => c.key !== item.key));
      }
      if (selected === item.key) onSelect(items.find((i) => i.isDefault)?.key ?? "standard");
      return;
    }

    // DB path — open confirmation dialog with dependency check
    setDeleteTarget(item.dbCat);
    setMoveToCategory("");
    setDependencies(null);
    setLoadingDeps(true);
    setDeleteDialogOpen(true);

    if (onGetDependencies) {
      const deps = await onGetDependencies(item.dbCat.id, item.dbCat.slug);
      setDependencies(deps);
    } else {
      setDependencies({ customerCount: 0, pricingRuleCount: 0, activityPriceCount: 0 });
    }
    setLoadingDeps(false);
  };

  const confirmDelete = () => {
    if (deleteTarget && onRemove) {
      onRemove(deleteTarget, moveToCategory || undefined);
    }
    setDeleteDialogOpen(false);
    if (selected === deleteTarget?.slug) onSelect(items.find((i) => i.isDefault)?.key ?? "standard");
    setDeleteTarget(null);
  };

  const openRename = (item: typeof items[0]) => {
    if (!item.dbCat) return;
    setRenameCat(item.dbCat);
    setRenameValue(item.label);
    setDailyLimitEnabled(item.dbCat.daily_minutes_limit != null);
    setDailyLimitMinutes(item.dbCat.daily_minutes_limit ?? 90);
    setDailyLimitFallback(item.dbCat.daily_limit_fallback_category ?? "standard");
    setOverflowPercent((item.dbCat as any).overflow_percent ?? 100);
    setRenameOpen(true);
  };

  const handleRename = () => {
    if (!renameCat || !renameValue.trim() || !onRename) return;
    onRename(renameCat, renameValue.trim());
    // Also save daily limit if callback provided
    if (onUpdateDailyLimit) {
      onUpdateDailyLimit(
        renameCat,
        dailyLimitEnabled ? dailyLimitMinutes : null,
        dailyLimitFallback,
        overflowPercent
      );
    }
    setRenameOpen(false);
    setRenameCat(null);
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {items.map((item) => (
        <div key={item.key} className="relative group">
          <Button
            variant={selected === item.key ? "default" : "outline"}
            size="sm"
            className="h-8 text-xs"
            onClick={() => onSelect(item.key)}
            onDoubleClick={() => editable && item.dbCat && openRename(item)}
          >
            {item.label}
          </Button>
          {editable && !item.isDefault && (
            <div className="absolute -top-1.5 -right-1.5 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              {item.dbCat && onRename && (
                <button
                  onClick={(e) => { e.stopPropagation(); openRename(item); }}
                  className="h-4 w-4 rounded-full bg-muted text-muted-foreground flex items-center justify-center"
                >
                  <Pencil className="h-2.5 w-2.5" />
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); handleRemove(item); }}
                className="h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          )}
        </div>
      ))}
      {editable && (
        <>
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setAddOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />Ny kategori
          </Button>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogContent className="max-w-xs">
              <DialogHeader><DialogTitle>Ny priskategori</DialogTitle></DialogHeader>
              <Input placeholder="T.ex. Student" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleAdd()} />
              <DialogFooter>
                <Button size="sm" onClick={handleAdd} disabled={!newLabel.trim()}>Lägg till</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>Redigera kategori</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1">
                  <Label className="text-xs">Namn</Label>
                  <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleRename()} />
                </div>
                <div className="border-t pt-3 space-y-3">
                  <Label className="text-xs font-medium">Daglig tidsgräns</Label>
                  <p className="text-[10px] text-muted-foreground -mt-1">
                    Begränsa hur många minuter/dag denna kategoris pris gäller. Överskjutande tid debiteras enligt fallback-kategorin.
                  </p>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={dailyLimitEnabled}
                      onCheckedChange={(v) => setDailyLimitEnabled(v)}
                    />
                    <span className="text-xs">Begränsa minuter per dag</span>
                  </div>
                  {dailyLimitEnabled && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Max minuter/dag</Label>
                        <NumberInput
                          min={15}
                          step={15}
                          value={dailyLimitMinutes}
                          onValueChange={(v) => setDailyLimitMinutes(v)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Efter gränsen, använd prislista</Label>
                        <Select value={dailyLimitFallback} onValueChange={setDailyLimitFallback}>
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(dbCategories ?? []).filter(c => c.slug !== renameCat?.slug).map(c => (
                              <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  {dailyLimitEnabled && (
                    <div className="space-y-1">
                      <Label className="text-xs">Pris efter dagsgräns</Label>
                      <div className="flex gap-2 items-center">
                        <NumberInput
                          min={0}
                          max={100}
                          value={overflowPercent}
                          onValueChange={(v) => setOverflowPercent(v)}
                          className="h-8 text-sm w-20"
                        />
                        <span className="text-xs text-muted-foreground">% av ordinarie banhyra</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        100% = fullpris efter gränsen. 50% = halva priset. 0% = gratis.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button size="sm" onClick={handleRename} disabled={!renameValue.trim()}>Spara</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Ta bort kategori &ldquo;{deleteTarget?.name}&rdquo;</DialogTitle>
          </DialogHeader>
          {loadingDeps ? (
            <p className="text-sm text-muted-foreground">Kontrollerar beroenden...</p>
          ) : dependencies && (
            <div className="space-y-3">
              {dependencies.customerCount > 0 && (
                <div className="space-y-2">
                  <p className="text-sm">
                    Kategorin har <strong>{dependencies.customerCount}</strong> kunder kopplade.
                    Välj ny kategori för dessa kunder:
                  </p>
                  <Select value={moveToCategory} onValueChange={setMoveToCategory}>
                    <SelectTrigger><SelectValue placeholder="Välj kategori..." /></SelectTrigger>
                    <SelectContent>
                      {(dbCategories ?? [])
                        .filter(c => c.id !== deleteTarget?.id)
                        .map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {(dependencies.pricingRuleCount > 0 || dependencies.activityPriceCount > 0) && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm">
                  <p className="font-medium text-destructive">Varning</p>
                  {dependencies.pricingRuleCount > 0 && (
                    <p>{dependencies.pricingRuleCount} prisregler använder denna kategori och kommer tas bort.</p>
                  )}
                  {dependencies.activityPriceCount > 0 && (
                    <p>{dependencies.activityPriceCount} aktivitetspriser använder denna kategori och kommer tas bort.</p>
                  )}
                </div>
              )}
              {dependencies.customerCount === 0 && dependencies.pricingRuleCount === 0 && dependencies.activityPriceCount === 0 && (
                <p className="text-sm text-muted-foreground">Inga beroenden hittades. Kategorin kan tas bort direkt.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Avbryt</Button>
            <Button
              variant="destructive"
              disabled={loadingDeps || ((dependencies?.customerCount ?? 0) > 0 && !moveToCategory)}
              onClick={confirmDelete}
            >
              {(dependencies?.customerCount ?? 0) > 0 ? "Flytta och ta bort" : "Ta bort"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
